import { useCallback, useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import moment, { Moment } from 'moment';
import { useIntl } from 'react-intl';
import Spacings from '~/modules/ui-kit/spacings';
import messages from './messages';
import styles from './styles.module.css';

type DisplayType = 'week' | 'day';

type CalendarControlsProps = {
  date: Moment;
  displayType?: DisplayType;
  onChange: (date: Date) => void;
};

const CalendarControls = ({
  date,
  displayType = 'week',
  onChange,
}: CalendarControlsProps) => {
  const intl = useIntl();
  const calendarLabelRef = useRef<HTMLDivElement | null>(null);
  const flatpickrInstance = useRef<flatpickr.Instance | undefined>(undefined);

  const handleTimePickerChange = useCallback(
    (event: null, date: Date) => {
      onChange(date);
    },
    [onChange]
  );

  useEffect(() => {
    if (calendarLabelRef.current) {
      flatpickrInstance.current = flatpickr(calendarLabelRef.current, {
        defaultDate: moment(date).toDate(),
        onChange: ([value]) => handleTimePickerChange(null, value),
        disableMobile: true,
        locale: {
          firstDayOfWeek: moment().startOf('week').isoWeekday(),
        },
      });
    }
  }, [date, handleTimePickerChange]);

  const onPrevious = () => {
    const previousDate = moment(date).subtract(1, displayType);
    if (flatpickrInstance.current) {
      flatpickrInstance.current.setDate(previousDate.toDate());
    }
    onChange(previousDate.toDate());
  };

  const onNext = () => {
    const nextDate = moment(date).add(1, displayType);
    if (flatpickrInstance.current) {
      flatpickrInstance.current.setDate(nextDate.toDate());
    }
    onChange(nextDate.toDate());
  };

  const getNextLabel = () =>
    displayType === 'day' ? messages.nextDay : messages.nextWeek;

  const getPreviousLabel = () =>
    displayType === 'day' ? messages.previousDay : messages.previousWeek;

  const renderWeekLabel = () => {
    const firstDay = date.weekday(0).format('L');
    const lastDay = date.weekday(6).format('L');
    return `${firstDay} - ${lastDay}`;
  };

  const renderDayLabel = () => {
    return date.format('LL');
  };

  const renderLabel = (displayType: DisplayType) =>
    displayType === 'day' ? renderDayLabel() : renderWeekLabel();

  return (
    <Spacings.Inline scale="l" justifyContent="center" alignItems="center">
      <div>
        <Tooltip title={intl.formatMessage(getPreviousLabel())}>
          <IconButton
            onClick={onPrevious}
            aria-label={intl.formatMessage(getPreviousLabel())}
            size="large"
          >
            <ArrowLeftIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className={styles['label-wrapper']}>
        <div className={styles.label} ref={calendarLabelRef}>
          <Typography>{renderLabel(displayType)}</Typography>
        </div>
      </div>
      <div>
        <Tooltip title={intl.formatMessage(getNextLabel())}>
          <IconButton
            onClick={onNext}
            aria-label={intl.formatMessage(getNextLabel())}
            size="large"
          >
            <ArrowRightIcon />
          </IconButton>
        </Tooltip>
      </div>
    </Spacings.Inline>
  );
};

export default CalendarControls;
